import { DashboardWidgetComparisonType } from 'src/constants/dashboard';
import type {
  ApiDashboardAggregate,
  DashboardWidgetConfig,
  FocalAndComparisonAggregate,
  UpstreamApiDashboardData,
  UpstreamApiDashboardResult,
} from 'src/types/dashboard';

/**
 * Get aggregate by question.
 */
export const getAggregateByQuestion = (
  question: string | null | undefined,
  dashboardResult?: UpstreamApiDashboardResult,
): ApiDashboardAggregate | undefined => {
  if (!dashboardResult || !dashboardResult.aggregates) return undefined;
  if (question?.includes(':')) {
    const [listName, rankAsString] = question.split(':');
    const rank = Number(rankAsString);
    const { question: questionFromList } =
      dashboardResult.sorted_item_lists.find(
        (item) => item.list_name === listName && item.rank === rank,
      ) || {};

    return getAggregateByQuestion(questionFromList, dashboardResult);
  }

  return dashboardResult.aggregates.find(
    (aggregate) => aggregate.question === question,
  );
};

/**
 * Get a list of focal and comparison aggregate pairs.
 */
export const getFocalAndComparisonAggregates = (
  config: DashboardWidgetConfig,
  data?: UpstreamApiDashboardData,
): FocalAndComparisonAggregate[] =>
  config.questions.map((question) => {
    const focalAggregate = getAggregateByQuestion(question, data?.focal_result);

    const dashboardResult =
      data && config.comparison_type === DashboardWidgetComparisonType.ANCESTOR
        ? data.ancestor_result
        : data?.children_results[0];

    const comparisonAggregate = getAggregateByQuestion(
      focalAggregate?.question,
      dashboardResult,
    );

    return {
      comparisonAggregate,
      focalAggregate,
    };
  });

/**
 * Get comparison data.
 */
export const getComparisonData = (
  config: DashboardWidgetConfig,
  data: UpstreamApiDashboardData,
) =>
  config.comparison_type === DashboardWidgetComparisonType.ANCESTOR
    ? data.ancestor_result && [data.ancestor_result]
    : data.focal_result &&
      data.children_results && [data.focal_result, ...data.children_results];

/**
 * Determines if the provided `comparisonDiff` should be evaluated (and colored) as either
 * a neutral, positive or negative change.
 *
 * If the provided value is not a `number` it is evaluated as neutral.
 *
 * Changes evaluated as neutral return `0`
 * Changes evaluated as positive return `1`
 * Changes evaluated as negative return `-1`
 */
export const isPositiveOrNegative = (
  comparisonDiff: number | null | undefined,
) => {
  if (typeof comparisonDiff !== 'number') return 0;

  if (comparisonDiff >= 5) return 1;

  if (comparisonDiff <= -5) return -1;

  return 0;
};

export const hasAggregates = (
  data: UpstreamApiDashboardData | null | undefined,
): boolean => {
  if (!data || !data.focal_result || !data.focal_result.aggregates) {
    return false;
  }
  return data.focal_result.aggregates.length > 0;
};

export const dataErrorHandling = (error: any) => {
  if (!error?.response) {
    return Promise.reject(String(error));
  }
  if (error.response.status >= 500 || error.response.status === 424) {
    // Error 424 indicates an issue with the reporting engine
    return Promise.reject('somethingWentWrong');
  }
  if (Object.hasOwn(error.response.data, 'detail')) {
    return Promise.reject(String(error.response.data.detail));
  }
  return Promise.reject(String(error.response.data));
};
